import { createFileRoute, redirect } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { PartyCard, PARTY_CARD_TABS, type PartyCardTab } from "@/features/parties/PartyCard";
import { getParty, partiesKeys } from "@/features/parties/parties.api";
import { Skeleton } from "@/components/feedback/Skeleton";
import { hasModule, firstAllowedRoute } from "@/lib/auth";

export const Route = createFileRoute("/_shell/parties/$id")({
  beforeLoad: ({ context }) => {
    if (!hasModule(context.session, "parties")) {
      throw redirect({ to: firstAllowedRoute(context.session) });
    }
  },
  validateSearch: (search: Record<string, unknown>): { tab?: PartyCardTab } => ({
    tab: PARTY_CARD_TABS.find((tab) => tab === search.tab),
  }),
  component: PartyCardScreen,
});

function PartyCardScreen() {
  const { t } = useTranslation();
  const { id } = Route.useParams();
  const { tab } = Route.useSearch();
  const navigate = Route.useNavigate();
  const { session } = Route.useRouteContext();
  const isAdmin = session.roles.includes("admin");
  // What AFAS holds is read-only for everyone; parties_write sets WLX's own settings.
  const canEditSettings = isAdmin || hasModule(session, "parties_write");
  const canSeeDossiers = hasModule(session, "dossiers") || hasModule(session, "dossiers_write");
  const canManagePortal = hasModule(session, "partner_users");
  const canSeePallets = hasModule(session, "tms_orders") || hasModule(session, "tms_orders_write") || hasModule(session, "pallet_accounts_write");

  const query = useQuery({
    queryKey: partiesKeys.detail(id),
    queryFn: () => getParty(id),
    retry: false,
  });

  if (query.isPending) {
    return (
      <div className="wl-page" data-screen-label="Back-office Party Card" aria-busy="true">
        <div style={{ marginBottom: "24px" }}>
          <Skeleton width="80px" height="var(--space-3)" />
          <div style={{ marginTop: "10px" }}>
            <Skeleton width="280px" height="var(--space-7)" />
          </div>
        </div>
        <Skeleton width="360px" height="var(--space-6)" />
        <div style={{ marginTop: "20px" }}>
          <Skeleton height="320px" />
        </div>
      </div>
    );
  }

  if (query.isError || !query.data) {
    return (
      <div className="wl-page" data-screen-label="Back-office Party Card">
        <h1 className="t-h2">{t("parties.notFound")}</h1>
        <p className="t-body-sm t-muted" style={{ marginTop: "4px" }}>
          {t("parties.notFoundDesc")}
        </p>
      </div>
    );
  }

  return (
    <div className="wl-page" data-screen-label="Back-office Party Card">
      <PartyCard
        party={query.data}
        canEditSettings={canEditSettings}
        isAdmin={isAdmin}
        canSeeDossiers={canSeeDossiers}
        canManagePortal={canManagePortal}
        canSeePallets={canSeePallets}
        userId={session.userId}
        tab={tab ?? "general"}
        onTab={(next) => navigate({ search: { tab: next === "general" ? undefined : next }, replace: true })}
      />
    </div>
  );
}
